import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../../api/apiClient';
import InfoBox from '../InfoBox';

export default function MappingTab() {
  const { t } = useTranslation();
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await apiService.listAuditMappings();
      setList(r);
      if (r?.items?.length && !selected) setSelected(r.items[0].name);
    } catch (e) {
      setError(e?.message || 'Network error');
      setList(null);
    } finally {
      setLoading(false);
    }
  }, [selected]);

  useEffect(() => { loadList(); }, [loadList]);

  return (
    <div>
      <InfoBox tabKey="mapping" />

      {loading && <div className="text-ink-muted">···</div>}
      {!loading && error && (
        <div className="rounded border border-signal-error/40 bg-signal-error/10 text-signal-error px-4 py-3 text-sm">
          {error}
          <button onClick={loadList} className="ml-3 underline hover:no-underline">
            {t('ui:refresh')}
          </button>
        </div>
      )}

      {!loading && !error && list && (
        <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] gap-4">
          <aside className="space-y-2">
            <div className="text-label tracking-widest text-ink-muted uppercase px-1">
              {t('traceability:mapping.available', { count: list.total })}
            </div>
            {(list.items || []).map((it) => (
              <button
                key={it.name}
                type="button"
                onClick={() => setSelected(it.name)}
                className={[
                  'w-full text-left rounded-lg border p-4 transition-colors',
                  selected === it.name
                    ? 'border-accent/60 bg-accent/10'
                    : 'border-border-subtle bg-surface-raised/40 hover:border-border-strong',
                ].join(' ')}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="text-sm font-semibold text-ink truncate">
                    {it.neo4j_label || it.entity_type_label || it.name}
                  </div>
                  <span className="text-[10px] tracking-widest text-ink-muted font-mono">
                    {it.entity_type || '—'}
                  </span>
                </div>
                <div className="mt-1 text-xs text-ink-muted font-mono truncate">{it.name}</div>
                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-secondary">
                  <span>
                    <span className="text-ink-muted">{t('traceability:mapping.columns')}:</span>{' '}
                    {it.columns_mapped}/{it.columns_total}
                  </span>
                  <span>
                    <span className="text-ink-muted">{t('traceability:mapping.documents')}:</span>{' '}
                    {it.documents_ingested}
                  </span>
                </div>
              </button>
            ))}

            {list.orphan_usage?.length > 0 && (
              <div className="mt-3 rounded border border-accent/40 bg-accent/5 p-3 text-xs">
                <div className="text-label tracking-widest text-accent-text uppercase mb-1">
                  {t('traceability:mapping.orphans')}
                </div>
                <ul className="text-ink-secondary font-mono text-[11px] space-y-0.5">
                  {list.orphan_usage.map((n) => <li key={n}>· {n}</li>)}
                </ul>
              </div>
            )}
          </aside>

          <section>
            {selected && <MappingDetail name={selected} />}
          </section>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// MappingDetail — the full, explanatory view for a single mapping.
// ---------------------------------------------------------------------------

function MappingDetail({ name }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const r = await apiService.getAuditMapping(name);
        if (!cancelled) setData(r);
      } catch (e) {
        if (!cancelled) { setError(e?.message || 'Network error'); setData(null); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [name]);

  if (loading) return <div className="text-ink-muted">···</div>;
  if (error) return (
    <div className="rounded border border-signal-error/40 bg-signal-error/10 text-signal-error px-4 py-3 text-sm">
      {error}
    </div>
  );
  if (!data) return null;

  const m = data.mapping || {};
  const meta = m.metadata || {};
  const neo = m.neo4j_config || {};
  const tables = m.table_mappings || {};
  const aliases = m.label_aliases || [];
  const semantic = m.semantic_patterns || {};
  const graph = data.graph_impact || {};
  const validation = m.validation_rules || {};
  const quality = m.data_quality_rules || {};

  return (
    <div className="space-y-5">
      <IdentityCard data={data} m={m} meta={meta} neo={neo} />
      <SelectionCard m={m} neo={neo} aliases={aliases} tables={tables} />
      <Neo4jCard neo={neo} />
      {Object.keys(semantic).length > 0 && <SemanticPatternsCard patterns={semantic} />}
      <GraphImpactCard graph={graph} neoLabel={neo.label} />
      <PipelineUsageCard m={m} neo={neo} tables={tables} />

      {Object.entries(tables).map(([tableName, tdef]) => (
        <TableCard key={tableName} name={tableName} def={tdef} />
      ))}

      {(Object.keys(validation).length > 0 || Object.keys(quality).length > 0) && (
        <ValidationCard validation={validation} quality={quality} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Identity: who/what/when. Shows the metadata block the user rarely reads.
// ---------------------------------------------------------------------------

function IdentityCard({ data, m, meta, neo }) {
  const { t } = useTranslation();
  const gi = data.graph_impact || {};
  const totalNodes = Object.values(gi.nodes_by_label || {}).reduce((a, b) => a + b, 0);

  return (
    <article className="rounded-lg border border-border-subtle bg-surface-raised/40 p-5">
      <header className="flex items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <div className="text-label tracking-widest text-ink-muted uppercase font-mono truncate">
            {data.name}
          </div>
          <h2 className="text-lg font-semibold text-ink mt-1">
            {m.entity_type_label || neo.label || data.name}
          </h2>
          {meta.description && (
            <p className="text-sm text-ink-secondary mt-1 leading-relaxed">{meta.description}</p>
          )}
        </div>
        <div className="text-xs text-ink-secondary text-right shrink-0">
          <div>{t('traceability:mapping.documents')}: <b className="text-ink">{data.usage?.documents_ingested ?? 0}</b></div>
          <div>{t('traceability:mapping.nodesInGraph')}: <b className="text-ink">{totalNodes}</b></div>
          <div className="text-ink-muted mt-1">{data.usage?.last_ingested_at || '—'}</div>
        </div>
      </header>

      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
        <dt className="text-ink-muted">{t('traceability:mapping.entityType')}</dt>
        <dd className="font-mono text-ink-secondary">
          {m.entity_type || '—'}
          {m.entity_type_label && <span className="ml-2 text-ink-muted">({m.entity_type_label})</span>}
        </dd>
        <dt className="text-ink-muted">{t('traceability:mapping.neo4jLabel')}</dt>
        <dd className="font-mono text-ink-secondary">{neo.label || '—'}</dd>
        {meta.source && <>
          <dt className="text-ink-muted">{t('traceability:mapping.source')}</dt>
          <dd className="font-mono text-ink-secondary">{meta.source}</dd>
        </>}
        {meta.version && <>
          <dt className="text-ink-muted">{t('traceability:mapping.version')}</dt>
          <dd className="font-mono text-ink-secondary">{meta.version}</dd>
        </>}
        {meta.created && <>
          <dt className="text-ink-muted">{t('traceability:mapping.created')}</dt>
          <dd className="font-mono text-ink-secondary">{meta.created}</dd>
        </>}
        {meta.author && <>
          <dt className="text-ink-muted">{t('traceability:mapping.author')}</dt>
          <dd className="text-ink-secondary">{meta.author}</dd>
        </>}
      </dl>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Selection: prose explanation of HOW the mapping engine picks this one.
// Mirrors the logic in backend/services/mapping_service.py::find_matching_mapping.
// ---------------------------------------------------------------------------

function SelectionCard({ m, neo, aliases, tables }) {
  const { t } = useTranslation();
  const tableNames = Object.keys(tables || {});

  return (
    <article className="rounded-lg border border-border-subtle bg-surface-raised/40 p-5">
      <SectionHeader heading={t('traceability:mapping.selection.heading')}
                     hint={t('traceability:mapping.selection.hint')} />

      <ol className="mt-3 space-y-3 text-sm">
        <SelectionStep
          n={1}
          title={t('traceability:mapping.selection.step1Title')}
          desc={t('traceability:mapping.selection.step1Desc')}
        >
          <BadgeRow items={tableNames} emptyLabel="—" mono />
        </SelectionStep>
        <SelectionStep
          n={2}
          title={t('traceability:mapping.selection.step2Title')}
          desc={t('traceability:mapping.selection.step2Desc')}
        >
          <BadgeRow items={m.entity_type ? [m.entity_type] : []} emptyLabel="—" mono accent />
        </SelectionStep>
        <SelectionStep
          n={3}
          title={t('traceability:mapping.selection.step3Title')}
          desc={t('traceability:mapping.selection.step3Desc')}
        >
          <BadgeRow items={aliases} emptyLabel="—" />
        </SelectionStep>
        <SelectionStep
          n={4}
          title={t('traceability:mapping.selection.fallbackTitle')}
          desc={t('traceability:mapping.selection.fallbackDesc')}
          muted
        />
      </ol>
    </article>
  );
}

function SelectionStep({ n, title, desc, children, muted }) {
  return (
    <li className="flex gap-3">
      <div className={[
        'shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-semibold',
        muted
          ? 'bg-surface border border-border-subtle text-ink-muted'
          : 'bg-accent/20 text-accent-text',
      ].join(' ')}>
        {muted ? '·' : n}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-ink text-sm font-medium">{title}</div>
        <div className="text-xs text-ink-secondary leading-relaxed mt-0.5">{desc}</div>
        {children && <div className="mt-2">{children}</div>}
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Neo4jCard: every field of neo4j_config. The "what lands in the graph" panel.
// ---------------------------------------------------------------------------

function Neo4jCard({ neo }) {
  const { t } = useTranslation();
  const qidLabels = neo.qid_labels || {};
  const indexes = neo.indexes || [];
  const mergeBlock = neo.merge_key_blocklist || [];
  const mergePrio = neo.merge_key_priority || [];
  const constraints = neo.constraints || [];

  return (
    <article className="rounded-lg border border-border-subtle bg-surface-raised/40 p-5">
      <SectionHeader heading={t('traceability:mapping.neo4j.heading')}
                     hint={t('traceability:mapping.neo4j.hint')} />

      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
        <SubSection title={t('traceability:mapping.neo4j.mergePriority')}
                    help={t('traceability:mapping.neo4j.mergePriorityHelp')}>
          {mergePrio.length > 0 ? (
            <ol className="text-xs space-y-0.5">
              {mergePrio.map((k, i) => (
                <li key={k} className="font-mono">
                  <span className="text-ink-muted mr-2">{i + 1}.</span>
                  <span className="text-ink">{k}</span>
                </li>
              ))}
            </ol>
          ) : <Empty />}
        </SubSection>

        <SubSection title={t('traceability:mapping.neo4j.mergeBlocklist')}
                    help={t('traceability:mapping.neo4j.mergeBlocklistHelp')}>
          <BadgeRow items={mergeBlock} tone="red" mono />
        </SubSection>

        <SubSection title={t('traceability:mapping.neo4j.constraints')}
                    help={t('traceability:mapping.neo4j.constraintsHelp')}>
          <BadgeRow items={constraints} mono />
        </SubSection>

        <SubSection title={t('traceability:mapping.neo4j.indexes')}
                    help={t('traceability:mapping.neo4j.indexesHelp')}>
          <BadgeRow items={indexes} mono accent />
        </SubSection>

        <SubSection title={t('traceability:mapping.neo4j.qidLabels')}
                    help={t('traceability:mapping.neo4j.qidLabelsHelp')}
                    span={2}>
          {Object.keys(qidLabels).length === 0 ? <Empty /> : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 text-xs">
              {Object.entries(qidLabels).map(([qid, lbl]) => (
                <div key={qid} className="flex items-baseline gap-2">
                  <span className="font-mono text-accent-text">{qid}</span>
                  <span className="text-ink-muted">→</span>
                  <span className="font-mono text-ink">{lbl}</span>
                </div>
              ))}
            </div>
          )}
        </SubSection>

        {'use_llm_for_cypher' in neo && (
          <SubSection title={t('traceability:mapping.neo4j.useLlmForCypher')}
                      help={t('traceability:mapping.neo4j.useLlmForCypherHelp')}
                      span={2}>
            <FlagBadge on={!!neo.use_llm_for_cypher} />
          </SubSection>
        )}
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// SemanticPatternsCard: the label→[P-codes] dictionary Gemini leans on.
// ---------------------------------------------------------------------------

function SemanticPatternsCard({ patterns }) {
  const { t } = useTranslation();
  return (
    <article className="rounded-lg border border-border-subtle bg-surface-raised/40 p-5">
      <SectionHeader heading={t('traceability:mapping.semantic.heading')}
                     hint={t('traceability:mapping.semantic.hint')} />
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
        {Object.entries(patterns).map(([name, codes]) => (
          <div key={name} className="rounded border border-border-subtle bg-surface p-3">
            <div className="text-label tracking-widest text-ink-muted uppercase mb-1">{name}</div>
            <div className="flex flex-wrap gap-1">
              {(codes || []).map((c) => (
                <span key={c} className="text-[11px] px-1.5 py-0.5 rounded bg-accent/10 border border-accent/40 text-accent-text font-mono">
                  {c}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// GraphImpactCard: live counts from the production graph. Answers
// "is anything actually in the graph for this mapping?".
// ---------------------------------------------------------------------------

function GraphImpactCard({ graph, neoLabel }) {
  const { t } = useTranslation();
  const nodes = graph.nodes_by_label || {};
  const rels = graph.relationships_by_type || {};
  const totalNodes = Object.values(nodes).reduce((a, b) => a + b, 0);
  const totalRels = Object.values(rels).reduce((a, b) => a + b, 0);

  return (
    <article className="rounded-lg border border-border-subtle bg-surface-raised/40 p-5">
      <SectionHeader heading={t('traceability:mapping.graphImpact.heading')}
                     hint={t('traceability:mapping.graphImpact.hint')} />

      {graph.error === 'graph_unavailable' && (
        <div className="mt-2 text-xs text-ink-muted">{t('traceability:mapping.graphImpact.unavailable')}</div>
      )}

      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
        <SubSection title={t('traceability:mapping.graphImpact.nodes', { count: totalNodes })}
                    help={t('traceability:mapping.graphImpact.nodesHelp')}>
          {Object.keys(nodes).length === 0 ? <Empty /> : (
            <ul className="text-xs space-y-0.5">
              {Object.entries(nodes).map(([lbl, n]) => (
                <li key={lbl} className="flex items-baseline justify-between gap-2">
                  <span className="font-mono text-ink">
                    {lbl}
                    {lbl === neoLabel && <span className="ml-1.5 text-[10px] text-accent-text">●</span>}
                  </span>
                  <span className="font-mono text-ink-secondary">{n}</span>
                </li>
              ))}
            </ul>
          )}
        </SubSection>

        <SubSection title={t('traceability:mapping.graphImpact.relationships', { count: totalRels })}
                    help={t('traceability:mapping.graphImpact.relationshipsHelp')}>
          {Object.keys(rels).length === 0 ? <Empty /> : (
            <ul className="text-xs space-y-0.5">
              {Object.entries(rels).map(([rt, n]) => (
                <li key={rt} className="flex items-baseline justify-between gap-2">
                  <span className="font-mono text-ink">{rt}</span>
                  <span className="font-mono text-ink-secondary">{n}</span>
                </li>
              ))}
            </ul>
          )}
        </SubSection>
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// PipelineUsageCard: prose about WHERE the mapping feeds the pipeline.
// Mirrors gemini_service.py transform_rows / propose_relationship_vocabulary
// / generate_mapping.
// ---------------------------------------------------------------------------

function PipelineUsageCard({ m, neo }) {
  const { t } = useTranslation();
  const stages = [
    {
      key: 'transform',
      ref: 'gemini_service.py · transform_rows',
    },
    {
      key: 'vocab',
      ref: 'gemini_service.py · propose_relationship_vocabulary',
    },
    {
      key: 'constraints',
      ref: 'graph_service.py · ensure_constraints',
    },
    {
      key: 'chat',
      ref: 'chat_service.py · answer',
    },
  ];

  return (
    <article className="rounded-lg border border-border-subtle bg-surface-raised/40 p-5">
      <SectionHeader heading={t('traceability:mapping.pipeline.heading')}
                     hint={t('traceability:mapping.pipeline.hint')} />

      <ol className="mt-3 space-y-3 text-sm">
        {stages.map((s, i) => (
          <li key={s.key} className="flex gap-3">
            <div className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-semibold bg-accent/20 text-accent-text">
              {i + 1}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-ink text-sm font-medium">
                {t(`traceability:mapping.pipeline.${s.key}Title`)}
              </div>
              <div className="text-xs text-ink-secondary leading-relaxed mt-0.5">
                {t(`traceability:mapping.pipeline.${s.key}Desc`)}
              </div>
              <code className="text-[10px] text-ink-muted font-mono mt-1 inline-block">{s.ref}</code>
            </div>
          </li>
        ))}
      </ol>
    </article>
  );
}

// ---------------------------------------------------------------------------
// TableCard: per-table drill-down with columns, entity classes and
// relationship block (when present).
// ---------------------------------------------------------------------------

function TableCard({ name, def }) {
  const { t } = useTranslation();
  const rows = useMemo(() => {
    if (!def || typeof def !== 'object') return [];
    const cols = def.column_mappings || def.wikidata_properties || {};
    return Object.entries(cols).map(([colName, spec]) => ({ colName, ...(spec || {}) }));
  }, [def]);
  const total = rows.length;
  const mapped = rows.filter((r) => r.wikidata_property).length;
  const pct = total ? Math.round((mapped / total) * 100) : 0;
  const entityClass = def?.entity_class;
  const entitySubclass = def?.entity_subclass;
  const rels = (def && typeof def === 'object' && def.relationships) || {};

  return (
    <article className="rounded-lg border border-border-subtle bg-surface-raised/40 p-5">
      <header className="flex items-baseline justify-between mb-2 gap-3">
        <div className="min-w-0">
          <div className="text-label tracking-widest text-ink-muted uppercase">
            {t('traceability:mapping.table')}
          </div>
          <h3 className="text-base font-semibold text-ink mt-0.5 font-mono truncate">{name}</h3>
        </div>
        <div className="text-xs text-ink-secondary shrink-0">
          {mapped}/{total} · {pct}%
        </div>
      </header>

      {(entityClass || entitySubclass) && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 mb-3 text-xs">
          {entityClass && (
            <div>
              <span className="text-ink-muted">{t('traceability:mapping.entityClass')}:</span>{' '}
              <span className="font-mono text-accent-text">{entityClass}</span>
            </div>
          )}
          {entitySubclass && (
            <div>
              <span className="text-ink-muted">{t('traceability:mapping.entitySubclass')}:</span>{' '}
              <span className="font-mono text-accent-text">{entitySubclass}</span>
            </div>
          )}
        </div>
      )}

      <div className="h-1 w-full bg-border-subtle rounded-full overflow-hidden mb-3">
        <div className="h-full bg-emerald-500 dark:bg-emerald-600 transition-[width]" style={{ width: `${pct}%` }} />
      </div>

      {total === 0 ? (
        <Empty />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-label tracking-widest text-ink-muted uppercase border-b border-border-subtle">
                <th className="py-1.5 pr-3 text-left">{t('traceability:mapping.column')}</th>
                <th className="py-1.5 pr-3 text-left">{t('traceability:mapping.property')}</th>
                <th className="py-1.5 pr-3 text-left">{t('traceability:mapping.label')}</th>
                <th className="py-1.5 pr-3 text-left">{t('traceability:mapping.dataType')}</th>
                <th className="py-1.5 pr-3 text-left">{t('traceability:mapping.transformations')}</th>
                <th className="py-1.5 pr-3 text-left">{t('traceability:mapping.defaultValue')}</th>
                <th className="py-1.5 pr-3 text-left">{t('traceability:mapping.required')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <React.Fragment key={r.colName}>
                  <tr className="border-b border-border-subtle/50 last:border-0 align-top">
                    <td className="py-1.5 pr-3 font-mono text-ink">{r.colName}</td>
                    <td className="py-1.5 pr-3 font-mono">
                      {r.wikidata_property
                        ? <span className="text-accent-text">{r.wikidata_property}</span>
                        : <span className="text-ink-muted">—</span>}
                    </td>
                    <td className="py-1.5 pr-3 text-ink-secondary">{r.property_label || '—'}</td>
                    <td className="py-1.5 pr-3 text-ink-muted">{r.data_type || '—'}</td>
                    <td className="py-1.5 pr-3 text-ink-muted">
                      {(r.transformations || []).join(', ') || '—'}
                    </td>
                    <td className="py-1.5 pr-3 text-ink-muted">
                      {r.default_value != null && r.default_value !== ''
                        ? <span className="font-mono">{String(r.default_value)}</span>
                        : '—'}
                    </td>
                    <td className="py-1.5 pr-3">
                      {r.required
                        ? <span className="text-emerald-600 dark:text-emerald-400">✓</span>
                        : <span className="text-ink-muted">·</span>}
                    </td>
                  </tr>
                  {(r.description || r.valid_values || r.pair_with) && (
                    <tr className="border-b border-border-subtle/30">
                      <td colSpan={7} className="pb-1.5 pl-0 text-[11px] text-ink-muted leading-snug">
                        {r.description && <div>{r.description}</div>}
                        {r.valid_values && (
                          <div className="mt-0.5">
                            <span className="uppercase tracking-widest mr-1">{t('traceability:mapping.validValues')}:</span>
                            {(Array.isArray(r.valid_values) ? r.valid_values : [r.valid_values]).map((v) => (
                              <code key={String(v)} className="mr-1 px-1 rounded bg-surface text-ink-secondary">{String(v)}</code>
                            ))}
                          </div>
                        )}
                        {r.pair_with && (
                          <div className="mt-0.5">
                            <span className="uppercase tracking-widest mr-1">{t('traceability:mapping.pairWith')}:</span>
                            <code className="px-1 rounded bg-surface text-ink-secondary">{String(r.pair_with)}</code>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {Object.keys(rels).length > 0 && (
        <div className="mt-4">
          <div className="text-label tracking-widest text-ink-muted uppercase mb-2">
            {t('traceability:mapping.relationships')}
          </div>
          <div className="space-y-1.5">
            {Object.entries(rels).map(([relName, r]) => (
              <div key={relName} className="rounded border border-border-subtle bg-surface p-2 text-xs">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <code className="text-ink font-medium">{relName}</code>
                  {r.neo4j_relationship_type && (
                    <span className="font-mono text-accent-text">→ {r.neo4j_relationship_type}</span>
                  )}
                  {r.property && (
                    <span className="font-mono text-ink-muted">· {r.property}</span>
                  )}
                  {r.target_class && (
                    <span className="font-mono text-ink-muted">· target: {r.target_class}</span>
                  )}
                  {r.edge_direction && (
                    <span className="text-ink-muted">· {r.edge_direction}</span>
                  )}
                </div>
                {r.description && (
                  <div className="text-ink-muted mt-0.5 leading-snug">{r.description}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </article>
  );
}

// ---------------------------------------------------------------------------
// ValidationCard: validation_rules + data_quality_rules. Renders whatever is
// actually present — these blocks are optional per mapping.
// ---------------------------------------------------------------------------

function ValidationCard({ validation, quality }) {
  const { t } = useTranslation();
  const required = validation.required_fields || [];
  const date = validation.date_validation || null;
  const url = validation.url_validation || null;
  const consistency = validation.consistency_checks || [];
  const completeness = quality.completeness_threshold;
  const accuracy = quality.accuracy_checks || [];
  const content = quality.content_quality || null;

  return (
    <article className="rounded-lg border border-border-subtle bg-surface-raised/40 p-5">
      <SectionHeader heading={t('traceability:mapping.validation.heading')}
                     hint={t('traceability:mapping.validation.hint')} />

      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
        {required.length > 0 && (
          <SubSection title={t('traceability:mapping.validation.requiredFields')}>
            <BadgeRow items={required} mono />
          </SubSection>
        )}

        {date && (
          <SubSection title={t('traceability:mapping.validation.dateRange')}>
            <div className="text-xs text-ink-secondary font-mono">
              {date.min_year ?? '—'} … {date.max_year ?? '—'}
            </div>
          </SubSection>
        )}

        {url && (
          <SubSection title={t('traceability:mapping.validation.urls')} span={2}>
            <div className="text-xs text-ink-secondary">
              {url.require_protocol && (
                <span className="mr-3">{t('traceability:mapping.validation.requireProtocol')}</span>
              )}
              {Array.isArray(url.allowed_domains) && url.allowed_domains.length > 0 && (
                <span>
                  <span className="text-ink-muted mr-1">{t('traceability:mapping.validation.allowedDomains')}:</span>
                  {url.allowed_domains.map((d) => (
                    <code key={d} className="mr-1 px-1 rounded bg-surface font-mono">{d}</code>
                  ))}
                </span>
              )}
            </div>
          </SubSection>
        )}

        {consistency.length > 0 && (
          <SubSection title={t('traceability:mapping.validation.consistency')} span={2}>
            <ul className="text-xs space-y-1">
              {consistency.map((c, i) => (
                <li key={i} className="text-ink-secondary">
                  <span className="font-mono text-accent-text">{c.rule}</span>
                  {Array.isArray(c.fields) && (
                    <span className="text-ink-muted"> · {c.fields.join(' ↔ ')}</span>
                  )}
                  {c.error_message && (
                    <div className="text-ink-muted mt-0.5">{c.error_message}</div>
                  )}
                </li>
              ))}
            </ul>
          </SubSection>
        )}

        {completeness != null && (
          <SubSection title={t('traceability:mapping.validation.completeness')}>
            <div className="text-xs font-mono text-ink-secondary">
              ≥ {Math.round(Number(completeness) * 100)}%
            </div>
          </SubSection>
        )}

        {accuracy.length > 0 && (
          <SubSection title={t('traceability:mapping.validation.accuracy')}>
            <BadgeRow items={accuracy} mono />
          </SubSection>
        )}

        {content && (
          <SubSection title={t('traceability:mapping.validation.contentQuality')} span={2}>
            <dl className="text-xs grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
              {Object.entries(content).map(([k, v]) => (
                <React.Fragment key={k}>
                  <dt className="text-ink-muted font-mono">{k}</dt>
                  <dd className="text-ink-secondary font-mono">{String(v)}</dd>
                </React.Fragment>
              ))}
            </dl>
          </SubSection>
        )}
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------

function SectionHeader({ heading, hint }) {
  return (
    <header>
      <h3 className="text-sm font-semibold text-ink">{heading}</h3>
      {hint && <p className="text-xs text-ink-secondary mt-1 leading-relaxed">{hint}</p>}
    </header>
  );
}

function SubSection({ title, help, children, span }) {
  return (
    <div className={span === 2 ? 'md:col-span-2' : ''}>
      <div className="text-label tracking-widest text-ink-muted uppercase mb-1">{title}</div>
      {help && <div className="text-[11px] text-ink-muted mb-1.5 leading-snug">{help}</div>}
      {children}
    </div>
  );
}

function BadgeRow({ items, emptyLabel = '—', mono, accent, tone }) {
  if (!items || items.length === 0) {
    return <span className="text-xs text-ink-muted">{emptyLabel}</span>;
  }
  const base = 'text-xs px-2 py-0.5 rounded border';
  const color = tone === 'red'
    ? 'border-red-300/50 bg-red-500/5 text-red-700 dark:text-red-300'
    : accent
      ? 'border-accent/40 bg-accent/10 text-accent-text'
      : 'border-border-subtle bg-surface text-ink-secondary';
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((it) => (
        <span key={String(it)} className={`${base} ${color} ${mono ? 'font-mono' : ''}`}>
          {String(it)}
        </span>
      ))}
    </div>
  );
}

function FlagBadge({ on }) {
  return (
    <span className={[
      'text-xs px-2 py-0.5 rounded border font-mono',
      on
        ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
        : 'border-border-subtle bg-surface text-ink-muted',
    ].join(' ')}>
      {on ? 'enabled' : 'disabled'}
    </span>
  );
}

function Empty() {
  return <span className="text-xs text-ink-muted">—</span>;
}
